import { useMemo, useState } from "react";

import { QRCodeSVG } from "qrcode.react";

import { Button, Icon } from "@stellar/design-system";

import { DialogModal } from "@/components/DialogModal";

import { truncatePublicKey } from "@/helpers/coSigningView";

import "./CoSignerSigningModal.scss";

import VIAWalletLogoSrc from "@/assets/VIA-wallet-logo.svg";

type Intent = "review" | "remove";
type Phase = "idle" | "signing" | "confirmReject" | "result";
type Outcome = "approved" | "rejected" | "removed";

export interface CoSignerSigningTarget {
  user_id: string;
  name: string;
  email?: string;
  publicKey?: string;
  role: "OWNER" | "FINANCIAL_CONTROLLER";
}

interface CoSignerSigningModalProps {
  visible: boolean;
  /** `review` = pending signer (approve or reject). `remove` = active signer. */
  intent: Intent | null;
  target: CoSignerSigningTarget | null;
  /** Verbatim tenant/org display name — used in the specific result copy. */
  orgName?: string;
  onClose: () => void;
  /** Owner completed the QR "signing" for a pending-signer approval. */
  onApprove: () => void;
  /** Owner rejected the pending-signer request (no signing). */
  onReject: () => void;
  /** Owner completed the QR "signing" for a co-signer removal. */
  onRemove: () => void;
}

const WAIT_MS = 900;

/**
 * QR-gated signing modal for approving / rejecting / removing co-signers.
 *
 * Matches the standalone prototype (`Team members co-signing 2026-09-03`)
 * layout: bordered content card with the QR + Copy URI on the left and a
 * name/email/public-key definition list on the right. The intent flag
 * controls the header copy and the footer button:
 *
 *   review + idle  → `Approve {name} as a co-signer`      → Reject (destructive, left)
 *   remove + idle  → `Remove {name} as a co-signer?`      → Cancel (secondary, right)
 *
 * On QR click the QR area swaps to `Waiting for signature…`; after a short
 * fake latency the modal advances to a result screen:
 *
 *   approved → green ✓ + specific "can now sign" copy + Done
 *   removed  → title + specific "can no longer sign" copy + Done (no icon)
 *   rejected → title + specific "is not a co-signer" copy + Done (no icon)
 *
 * Reject requires a nested confirmation ("Reject {name} as a co-signer?")
 * before landing on the rejected result screen — modelled as a second
 * DialogModal stacked on top of the primary one.
 *
 * The commit callback (`onApprove` / `onReject` / `onRemove`) is invoked
 * when the user clicks Done on the result screen, not when the outcome is
 * decided. That gives the parent a single deterministic hook to mutate
 * state after the modal is confirmed dismissed.
 *
 * TODO(SDP-multisig): swap the QR contents for a real SEP-7 URI when the
 * backend `POST /organizations/multisig/co-signers/{id}/challenge` endpoint
 * ships, and replace the `setTimeout` with a poll on
 * `GET .../challenge/status`.
 */
export const CoSignerSigningModal = ({
  visible,
  intent,
  target,
  orgName,
  onClose,
  onApprove,
  onReject,
  onRemove,
}: CoSignerSigningModalProps) => {
  const [phase, setPhase] = useState<Phase>("idle");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [copiedUri, setCopiedUri] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  const qrPayload = useMemo(() => {
    if (!intent || !target) return "";
    return `via-wallet-multisig://sign?intent=${intent}&target=${target.user_id}`;
  }, [intent, target]);

  if (!intent || !target) {
    return (
      <DialogModal visible={visible} onClose={onClose}>
        <div />
      </DialogModal>
    );
  }

  const commit = () => {
    if (!outcome) return;
    if (outcome === "approved") onApprove();
    else if (outcome === "rejected") onReject();
    else if (outcome === "removed") onRemove();
    onClose();
  };

  const beginSigning = () => {
    if (phase !== "idle") return;
    setPhase("signing");
    window.setTimeout(() => {
      setOutcome(intent === "review" ? "approved" : "removed");
      setPhase("result");
    }, WAIT_MS);
  };

  const openRejectConfirm = () => {
    if (phase !== "idle") return;
    setPhase("confirmReject");
  };

  const confirmReject = () => {
    setOutcome("rejected");
    setPhase("result");
  };

  const backToReview = () => setPhase("idle");

  const copyUri = () => {
    if (!qrPayload) return;
    void navigator.clipboard.writeText(qrPayload).then(() => {
      setCopiedUri(true);
      window.setTimeout(() => setCopiedUri(false), 1600);
    });
  };

  const copyKey = () => {
    if (!target.publicKey) return;
    void navigator.clipboard.writeText(target.publicKey).then(() => {
      setCopiedKey(true);
      window.setTimeout(() => setCopiedKey(false), 1600);
    });
  };

  const title = renderTitle(intent, phase, outcome, target.name);
  const isResult = phase === "result";
  const resultMessage = renderResultMessage(outcome, target.name, orgName);

  return (
    <>
      <DialogModal visible={visible && !isResult} onClose={onClose} title={title} width="60">
        <IdleOrSigningBody
          intent={intent}
          target={target}
          phase={phase === "confirmReject" ? "idle" : phase}
          qrPayload={qrPayload}
          copiedUri={copiedUri}
          copiedKey={copiedKey}
          onQrClick={beginSigning}
          onCopyUri={copyUri}
          onCopyKey={copyKey}
          onReject={openRejectConfirm}
          onCancel={onClose}
        />
      </DialogModal>

      <DialogModal
        visible={phase === "confirmReject"}
        xButton={false}
        width="50"
        onClose={backToReview}
        title={`Reject ${target.name} as a co-signer?`}
        footerAlignment="stretch"
        primaryAction={{
          label: "Keep reviewing",
          variant: "tertiary",
          onClick: backToReview,
        }}
        secondaryAction={{
          label: `Reject co-signer`,
          variant: "destructive",
          onClick: confirmReject,
        }}
      >
        <p style={{ color: "var(--sds-clr-gray-11)" }}>
          They won&apos;t be able to sign disbursements. Their co-signing key stays in VIA Wallet
          but won&apos;t be used for co-signing. You can invite them again later.
        </p>
      </DialogModal>

      <DialogModal
        visible={isResult}
        width="50"
        onClose={commit}
        xButton={false}
        title={
          <div className="CoSignerSigningModal__resultTitle">
            {outcome === "approved" ? (
              <span className="CoSignerSigningModal__resultIcon" aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  name="check"
                >
                  <path d="M20 6 9 17l-5-5"></path>
                </svg>
              </span>
            ) : null}
            <span>{title}</span>
          </div>
        }
        primaryAction={{
          label: "Done",
          variant: "tertiary",
          onClick: commit,
        }}
      >
        <div className="CoSignerSigningModal">
          <p className="CoSignerSigningModal__resultBody" role="status" aria-live="polite">
            {resultMessage}
          </p>
        </div>
      </DialogModal>
    </>
  );
};

/**
 * The bordered "signing card" body: two-column QR + details layout, plus a
 * footer button row. During signing, the QR area swaps for a spinner + a
 * `Waiting for signature…` line while the rest of the card stays put.
 */
const IdleOrSigningBody = ({
  intent,
  target,
  phase,
  qrPayload,
  copiedUri,
  copiedKey,
  onQrClick,
  onCopyUri,
  onCopyKey,
  onReject,
  onCancel,
}: {
  intent: Intent;
  target: CoSignerSigningTarget;
  phase: Phase;
  qrPayload: string;
  copiedUri: boolean;
  copiedKey: boolean;
  onQrClick: () => void;
  onCopyUri: () => void;
  onCopyKey: () => void;
  onReject: () => void;
  onCancel: () => void;
}) => {
  const helper =
    intent === "review"
      ? "Co-signers help sign disbursements, so a co-signing Owner needs to approve this in VIA Wallet. Scan the QR code with VIA Wallet to review and approve."
      : "Removing a co-signer changes who can sign disbursements, so a co-signing Owner needs to approve this in VIA Wallet. Scan the QR code with VIA Wallet to review and approve.";

  const isSigning = phase === "signing";
  return (
    <div className="CoSignerSigningModal">
      <p className="CoSignerSigningModal__helper">{helper}</p>

      <div className="CoSignerSigningModal__card">
        <div className="CoSignerSigningModal__qrCol">
          {isSigning ? (
            <div className="CoSignerSigningModal__qrWaiting" role="status" aria-live="polite">
              <span className="CoSignerSigningModal__spinner" aria-hidden="true" />
              <span>Waiting for signature…</span>
            </div>
          ) : (
            // {/* TODO: sdp-fe - remove the button, use WalletConnect instead. for exactly next task. */}
            <button
              type="button"
              className="CoSignerSigningModal__qr"
              aria-label="Simulate signing on VIA Wallet"
              onClick={onQrClick}
            >
              <QRCodeSVG
                value={qrPayload}
                size={148}
                level="M"
                includeMargin={false}
                imageSettings={{
                  src: VIAWalletLogoSrc,
                  height: 25,
                  width: 25,
                  excavate: true,
                }}
              />
            </button>
          )}
          <button
            type="button"
            className="CoSignerSigningModal__copyUri"
            onClick={onCopyUri}
            disabled={isSigning}
          >
            <span className="CoSignerSigningModal__copyUriIcon" aria-hidden="true">
              {copiedUri ? <Icon.Check /> : <Icon.Copy01 />}
            </span>
            <span>{copiedUri ? "Copied" : "Copy URI"}</span>
          </button>
        </div>

        <dl className="CoSignerSigningModal__details">
          <div>
            <dt>Name</dt>
            <dd>{target.name}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{target.email ?? "—"}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd>
              {target.role
                ? target.role
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/^\w/, (c) => c.toUpperCase())
                : "—"}
            </dd>
          </div>

          <div>
            <dt>Public key address</dt>
            <dd>
              {target?.publicKey ? (
                <span className="CoSignerSigningModal__key">
                  <code title={target.publicKey}>{truncatePublicKey(target.publicKey)}</code>
                  <button
                    type="button"
                    className="CoSignerSigningModal__keyCopy"
                    aria-label="Copy public key"
                    onClick={onCopyKey}
                  >
                    {copiedKey ? <Icon.Check /> : <Icon.Copy01 />}
                  </button>
                </span>
              ) : (
                "—"
              )}
            </dd>
          </div>
        </dl>
      </div>

      <div
        className={
          intent === "review"
            ? "CoSignerSigningModal__actions CoSignerSigningModal__actions--left"
            : "CoSignerSigningModal__actions CoSignerSigningModal__actions--right"
        }
      >
        {intent === "review" ? (
          <Button size="md" variant="destructive" onClick={onReject} disabled={isSigning}>
            Reject
          </Button>
        ) : (
          <Button size="md" variant="tertiary" onClick={onCancel} disabled={isSigning}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

const renderTitle = (
  intent: Intent,
  phase: Phase,
  outcome: Outcome | null,
  name: string,
): string => {
  if (phase === "result") {
    if (outcome === "approved") return "Co-signer approved";
    if (outcome === "removed") return "Co-signer removed";
    return "Co-signer rejected";
  }
  return intent === "review" ? `Approve ${name} as a co-signer` : `Remove ${name} as a co-signer?`;
};

const renderResultMessage = (outcome: Outcome | null, name: string, orgName?: string): string => {
  const tenant = orgName ?? "your organization";
  if (outcome === "approved") {
    return `${name} can now sign disbursements for ${tenant}. Each disbursement needs two co-signers before funds go out.`;
  }
  if (outcome === "removed") {
    return `${name} can no longer sign disbursements for ${tenant}. Their co-signing key stays in VIA Wallet but won't be used for co-signing. You can invite them again anytime.`;
  }
  return `${name} is not a co-signer for ${tenant}. Their co-signing key stays in VIA Wallet but won't be used for co-signing. You can invite them again anytime.`;
};
